import { ChannelDoc, HealthState } from '@monitor/shared';

export type DisplayTone = 'ok' | 'warn' | 'bad' | 'neutral';

export interface ChannelStatus {
  /** Effective badge state — includes client-derived `stale`. */
  state: HealthState;
  label: string;
  tone: DisplayTone;
  /** True when the channel's checks stopped completing (staleness, KTD8/R12). */
  stale: boolean;
}

const LABELS: Record<HealthState, string> = {
  healthy: 'Healthy',
  failing: 'Failing',
  degraded: 'Degraded',
  paused: 'Paused',
  stale: 'Stale',
  unknown: 'Unknown',
};

const TONES: Record<HealthState, DisplayTone> = {
  healthy: 'ok',
  failing: 'bad',
  degraded: 'warn',
  paused: 'neutral',
  stale: 'warn',
  unknown: 'neutral',
};

/**
 * Derive what the dashboard shows for a channel. Staleness is computed client-side
 * so a channel whose checks stopped completing reads as `stale` (a warning), never
 * a stale-green `healthy` (R12). A paused channel always reads `paused`.
 */
export function deriveChannelStatus(ch: ChannelDoc, nowMs: number): ChannelStatus {
  if (ch.paused) {
    return { state: 'paused', label: LABELS.paused, tone: TONES.paused, stale: false };
  }
  const last = ch.health.lastCheckCompletedAt;
  const stale = last !== null && nowMs - last.toMillis() > 2 * ch.frequencyMinutes * 60_000;
  const effective: HealthState = ch.health.state === 'healthy' && stale ? 'stale' : ch.health.state;
  return { state: effective, label: LABELS[effective], tone: TONES[effective], stale };
}

/** Countdown text for a paused channel with a timed auto-resume (R12). */
export function resumeCountdown(ch: ChannelDoc, nowMs: number): string | null {
  if (!ch.paused || !ch.resumeAt) return null;
  const ms = ch.resumeAt.toMillis() - nowMs;
  if (ms <= 0) return 'resuming shortly';
  const mins = Math.ceil(ms / 60_000);
  if (mins < 60) return `auto-resumes in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `auto-resumes in ${hrs}h ${mins % 60}m`;
}

/** Board-level warning when no channels are configured (never render as all-green). */
export function isEmptyProjectSet(channelCount: number): boolean {
  return channelCount === 0;
}
